import React, { useCallback, useMemo, useState } from 'react';
import { IMAGES } from '../../../utils/images';
import { isViteDevelopmentMode } from '../../../config/env';
import { useGameStateContext } from '../../context/useGameStateContext';
import type { PlayerLetter } from '../../types/game.types';
import { GameSettings } from './GameSettings';

import './GameProfile.css';

export interface GameProfileProps {
    username: string;
    /** Current seat in the room, if assigned */
    playerLetter: PlayerLetter | null;
}

/**
 * Top-right game header: username, subtitle (rank title like lobby), settings trigger, and modal.
 * In Vite development mode, subtitle includes "Player A/B" before the title.
 * Board phases: styled by `GamePlay.css` (`.top_bar_right`); lobby uses `LobbyPhase.css`.
 */
export const GameProfile: React.FC<GameProfileProps> = ({ username, playerLetter }) => {
    const { players } = useGameStateContext();
    const [settingsOpen, setSettingsOpen] = useState(false);

    const openSettings = useCallback(() => setSettingsOpen(true), []);
    const closeSettings = useCallback(() => setSettingsOpen(false), []);

    const subtitle = useMemo(() => {
        const rankTitle =
            playerLetter != null
                ? players.find((p) => p.id === playerLetter)?.title || 'Cadet'
                : 'Cadet';
        if (isViteDevelopmentMode && playerLetter != null) {
            return `Player ${playerLetter} · ${rankTitle}`;
        }
        return rankTitle;
    }, [players, playerLetter]);

    return (
        <>
            <div className="top_bar_right">
                <div className="top_bar_text">
                    <p className="top_bar_name">{username}</p>
                    <p className="top_bar_title">{subtitle}</p>
                </div>
                <button
                    type="button"
                    className="top_bar_profile"
                    onClick={openSettings}
                    aria-label="Open settings"
                    aria-haspopup="dialog"
                    aria-expanded={settingsOpen}
                >
                    <img
                        src={IMAGES.profile_pics['default.png']}
                        className="profile_pic"
                        alt=""
                    />
                </button>
            </div>
            <GameSettings open={settingsOpen} onClose={closeSettings} />
        </>
    );
};

export default GameProfile;
